import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Card, Container, Spinner } from "react-bootstrap"

import { resumeSignup, getSecurityQuestions } from "@api/authenticate"
import { ErrorHandling } from "@helpers/GlobalFunctions"
import SetupSecurityModal from "@components/SetupSecurityModal/SetupSecurityModal"
import { Header } from "@components/Header"
import { toast } from "@components/ToastExtended"

const ContinueSignup = () => {
    const { continueToken } = useParams()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [setupData, setSetupData] = useState(null)
    const [setupStep, setSetupStep] = useState(null)
    const [securityQuestionsList, setSecurityQuestionsList] = useState([])
    const [totpProvisioningUri, setTotpProvisioningUri] = useState(null)

    useEffect(() => {
        const loadSignup = async () => {
            try {
                // Resume signup with the token from URL
                const response = await resumeSignup(continueToken)
                const data = response.data

                // data contains:
                // - setup_token (to pass to SetupSecurityModal)
                // - email
                // - questions_configured (bool)
                // - mfa_configured (bool)
                // - next_step: "questions" or "totp"

                setSetupData(data)
                setSetupStep(data.next_step)

                // Load security questions if needed
                if (data.next_step === "questions") {
                    const questionsResponse = await getSecurityQuestions()
                    setSecurityQuestionsList(questionsResponse?.data ?? [])
                }

                setLoading(false)
            } catch (err) {
                const errorMessage = ErrorHandling(err)
                setError(errorMessage || "Invalid or expired link. Please start signup again.")
                setLoading(false)
            }
        }

        if (continueToken) {
            loadSignup()
        } else {
            setError("No token provided")
            setLoading(false)
        }
    }, [continueToken])

    // Load questions when needed
    useEffect(() => {
        if (setupStep === "questions" && securityQuestionsList.length === 0) {
            getSecurityQuestions()
                .then((res) => setSecurityQuestionsList(res?.data ?? []))
                .catch(() => setSecurityQuestionsList([]))
        }
    }, [setupStep, securityQuestionsList.length])

    if (loading) {
        return (
            <>
                <Header />
                <div className="fixed-nav-margin">
                    <Container className="my-5 py-5 text-center">
                        <Spinner animation="border" role="status" className="mb-3">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="text-muted">Loading your signup...</p>
                    </Container>
                </div>
            </>
        )
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="fixed-nav-margin">
                    <Container className="my-5 py-5">
                        <Card className="shadow-lg mx-auto" style={{ maxWidth: "32rem" }}>
                            <Card.Body className="p-4 text-center">
                                <Card.Title className="text-danger mb-3">
                                    Unable to Resume Signup
                                </Card.Title>
                                <Card.Text className="mb-4">{error}</Card.Text>
                                <div className="d-grid gap-2">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate("/sign-up?account=candidate")}
                                    >
                                        Start New Signup
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => navigate("/")}
                                    >
                                        Go to Home
                                    </button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Container>
                </div>
            </>
        )
    }

    return (
        <>
            <Header />
            <div className="fixed-nav-margin">
                <Container className="my-5 py-5">
                    <Card className="shadow-lg mx-auto" style={{ maxWidth: "32rem" }}>
                        <Card.Body className="p-4">
                            <Card.Title className="mb-2">Welcome Back!</Card.Title>
                            <Card.Text className="text-muted small mb-4">
                                Continue your signup for <strong>{setupData?.email}</strong>
                            </Card.Text>
                            {setupStep === "questions" && (
                                <Card.Text className="text-muted small">
                                    Complete your security questions to proceed.
                                </Card.Text>
                            )}
                            {setupStep === "totp" && (
                                <Card.Text className="text-muted small">
                                    Set up Authenticator to complete your signup.
                                </Card.Text>
                            )}
                        </Card.Body>
                    </Card>
                </Container>
            </div>

            <SetupSecurityModal
                show={!!setupData}
                setupToken={setupData?.setup_token}
                setupStep={setupStep}
                setSetupStep={setSetupStep}
                securityQuestionsList={securityQuestionsList}
                totpProvisioningUri={totpProvisioningUri}
                setTotpProvisioningUri={setTotpProvisioningUri}
                onComplete={() => {
                    setSetupData(null)
                    setSetupStep(null)
                    setTotpProvisioningUri(null)
                    toast.show({
                        type: "success",
                        title: "Signup Complete!",
                        description: "Please check your email and verify your account before logging in."
                    })
                    navigate("/sign-in")
                }}
            />
        </>
    )
}

export default ContinueSignup
